import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Eye, EyeOff, Save, X } from 'lucide-react';
import { clsx } from 'clsx';
import ResponseAreaRenderer from '../document/ResponseAreaRenderer';

const normalizeValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || '').trim()).sort().join('||');
  }
  return String(value ?? '').trim().toLowerCase();
};

const getAutoScore = (config, responseValue) => {
  const points = Number(config?.points || 0);
  if (!config || !points) return '';

  if (config.type === 'multiple-choice') {
    if (!config.correctAnswer) return '';
    return normalizeValue(responseValue) === normalizeValue(config.correctAnswer) ? points : 0;
  }

  if (config.type === 'multi-select') {
    const correct = Array.isArray(config.correctAnswers) ? config.correctAnswers.filter(Boolean) : [];
    if (!correct.length) return '';
    return normalizeValue(responseValue) === normalizeValue(correct) ? points : 0;
  }

  if (config.type === 'short-answer') {
    if (!config.correctAnswer) return '';
    return normalizeValue(responseValue) === normalizeValue(config.correctAnswer) ? points : 0;
  }

  return '';
};

const getGradingStatusLabel = (submission = {}) => {
  const status = String(submission.gradingStatus || '').trim().toLowerCase();
  if (status === 'graded') return 'Graded';
  if (status === 'in-review') return 'Draft';
  if (status === 'submitted') return 'Pending';
  if (status === 'pending-submission') return 'Not Submitted';
  return status ? status.replace(/-/g, ' ') : 'Pending';
};

const SubmissionReviewModal = ({ submission, document, currentUser, onClose, onSave }) => {
  const assessment = document?.assessment || {};
  const responseOrder = assessment.responseAreaOrder || Object.keys(assessment.responseAreas || {});
  const responseAreas = assessment.responseAreas || {};
  const maxScore = responseOrder.reduce((sum, blockId) => sum + Number(responseAreas[blockId]?.points || 0), 0);
  const [manualScores, setManualScores] = useState({});
  const [feedback, setFeedback] = useState('');
  const [markerNotes, setMarkerNotes] = useState('');
  const [showModelAnswers, setShowModelAnswers] = useState(true);
  const [saveError, setSaveError] = useState('');
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);

  useEffect(() => {
    const nextScores = {};
    responseOrder.forEach((blockId) => {
      const config = responseAreas[blockId];
      const savedScore = submission?.manualScores?.[blockId];
      nextScores[blockId] = savedScore ?? getAutoScore(config, submission?.responses?.[blockId]?.value);
    });
    setManualScores(nextScores);
    setFeedback(submission?.graderFeedback || '');
    setMarkerNotes(submission?.markerNotes || '');
    setSaveError('');
  }, [responseAreas, responseOrder, submission]);

  const scoredTotal = useMemo(
    () => responseOrder.reduce((sum, blockId) => sum + Number(manualScores[blockId] || 0), 0),
    [manualScores, responseOrder]
  );
  const percentage = maxScore > 0 ? Math.round((scoredTotal / maxScore) * 1000) / 10 : 0;
  const missingScores = responseOrder.filter((blockId) => {
    const points = Number(responseAreas[blockId]?.points || 0);
    if (points <= 0) return false;
    return manualScores[blockId] === '' || manualScores[blockId] == null;
  });

  const handleScoreChange = (blockId, value) => {
    setManualScores((current) => ({
      ...current,
      [blockId]: value === '' ? '' : Math.max(0, Number(value)),
    }));
  };

  const buildGradePayload = (gradingStatus) => ({
    manualScores: Object.fromEntries(
      Object.entries(manualScores).map(([blockId, score]) => [blockId, score === '' ? null : Number(score || 0)])
    ),
    score: scoredTotal,
    percentage,
    graderFeedback: feedback,
    markerNotes,
    gradingStatus,
    gradedById: currentUser?.uid || null,
    gradedByName: currentUser?.displayName || currentUser?.email || '',
    gradedAt: gradingStatus === 'graded' ? new Date().toISOString() : submission?.gradedAt || null,
  });

  const handleSaveDraft = async () => {
    setIsSavingDraft(true);
    setSaveError('');
    try {
      await onSave(buildGradePayload('in-review'));
    } catch (error) {
      console.error('Failed to save grading draft:', error);
      setSaveError('Could not save grading draft.');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleFinalize = async () => {
    if (missingScores.length > 0) {
      setSaveError('Score every response block with marks before finalising.');
      return;
    }
    setIsFinalizing(true);
    setSaveError('');
    try {
      await onSave(buildGradePayload('graded'));
    } catch (error) {
      console.error('Failed to finalise grade:', error);
      setSaveError('Could not finalise grading.');
    } finally {
      setIsFinalizing(false);
    }
  };

  const submittedAtLabel = submission?.submittedAt?.seconds
    ? new Date(submission.submittedAt.seconds * 1000).toLocaleString()
    : submission?.submittedAt
      ? new Date(submission.submittedAt).toLocaleString()
      : 'Not submitted';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-sigma-700">
              {getGradingStatusLabel(submission)}
            </div>
            <h2 className="text-xl font-semibold text-gray-900">{submission?.learnerName || 'Learner Submission'}</h2>
            <p className="text-sm text-gray-500">{document?.title || document?.name || 'Assessment Document'}</p>
            <p className="mt-1 text-xs text-gray-500">Submitted: {submittedAtLabel}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid flex-1 min-h-0 grid-cols-1 gap-0 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="min-h-0 overflow-y-auto px-6 py-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="text-sm text-gray-600">
                Review learner responses, score each block, and optionally compare against model answers.
              </div>
              <button
                type="button"
                onClick={() => setShowModelAnswers((prev) => !prev)}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {showModelAnswers ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                {showModelAnswers ? 'Hide Model Answers' : 'Show Model Answers'}
              </button>
            </div>

            <div className="space-y-5">
              {responseOrder.map((blockId, index) => {
                const config = responseAreas[blockId];
                const points = Number(config?.points || 0);
                const value = submission?.responses?.[blockId]?.value ?? '';
                return (
                  <section key={blockId} className="rounded-xl border border-gray-200 bg-white p-4">
                    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-sigma-700">
                          Response Block {index + 1}
                        </div>
                        <h3 className="text-sm font-semibold text-gray-900">
                          {config?.label || config?.type?.replace(/-/g, ' ') || `Response ${index + 1}`}
                        </h3>
                        <div className="mt-1 text-xs text-gray-500">
                          Type: {config?.type || 'unknown'}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="text-xs text-gray-500">Marks</div>
                        <input
                          type="number"
                          min="0"
                          max={points}
                          step="0.5"
                          value={manualScores[blockId] ?? ''}
                          onChange={(e) => handleScoreChange(blockId, e.target.value)}
                          className="w-20 rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-100"
                        />
                        <div className="text-sm font-medium text-gray-600">/ {points}</div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-sigma-100 bg-sigma-50 p-4">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-sigma-700">Learner Response</div>
                      <ResponseAreaRenderer
                        blockId={blockId}
                        config={config}
                        value={value}
                        onChange={() => {}}
                        disabled
                        userId={submission?.learnerId}
                        showModelAnswer={showModelAnswers}
                      />
                    </div>
                  </section>
                );
              })}
            </div>
          </div>

          <aside className="min-h-0 overflow-y-auto border-t border-gray-200 bg-gray-50 px-6 py-5 lg:border-l lg:border-t-0">
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Summary</div>
              <div className="mt-2 flex items-end justify-between">
                <div>
                  <div className="text-3xl font-bold text-gray-900">{scoredTotal}<span className="text-lg text-gray-500">/{maxScore}</span></div>
                  <div className="text-sm text-gray-500">{percentage}%</div>
                </div>
                <div className={clsx(
                  'rounded-full px-3 py-1 text-xs font-semibold',
                  String(submission?.gradingStatus || '') === 'graded'
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-amber-100 text-amber-700'
                )}>
                  {getGradingStatusLabel(submission)}
                </div>
              </div>
              {missingScores.length > 0 && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {missingScores.length} response block(s) still need marks before finalising.
                </div>
              )}
            </div>

            <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">Feedback to Learner</label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={5}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-100"
                placeholder="Shared feedback for the learner"
              />
            </div>

            <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">Internal Marker Notes</label>
              <textarea
                value={markerNotes}
                onChange={(e) => setMarkerNotes(e.target.value)}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-100"
                placeholder="Private notes for marking handover or moderation"
              />
            </div>

            {saveError && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {saveError}
              </div>
            )}

            <div className="mt-5 flex flex-col gap-3">
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={isSavingDraft || isFinalizing}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
              >
                <Save className="h-4 w-4" />
                {isSavingDraft ? 'Saving Draft...' : 'Save Draft'}
              </button>
              <button
                type="button"
                onClick={handleFinalize}
                disabled={isSavingDraft || isFinalizing}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-sigma-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sigma-800 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                <CheckCircle2 className="h-4 w-4" />
                {isFinalizing ? 'Finalising...' : 'Finalise Grade'}
              </button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default SubmissionReviewModal;
